<template>
  <div
    class="wa-shell"
    :class="shellClasses"
  >
    <HeadNav selectedLanguage="id" :page="pageKey" :gotodownload="downloadApk" :apk="apk" />

    <main id="maincontent" class="wa-main">
      <nav
        class="breadcrumb"
        :class="breadcrumbClasses"
        aria-label="Breadcrumb"
      >
        <a href="/" v-if="pageKey !== 'home'">Home</a>
        <template v-if="isBlogArticle">
          <span>></span>
          <a href="/blog/">Blog</a>
          <span>></span>
          <span>{{ content.breadcrumb || content.title }}</span>
        </template>
        <template v-else-if="content.slug !== '/'">
          <span>></span>
          <span>{{ content.breadcrumb || content.title }}</span>
        </template>
      </nav>

      <section
        class="hero-section"
        :class="heroSectionClasses"
      >
        <div class="hero-copy">
          <p v-if="content.hero && content.hero.eyebrow" class="eyebrow">{{ content.hero.eyebrow }}</p>
          <h1>{{ content.title }}</h1>
          <p v-if="content.hero && content.hero.lead" class="hero-lead">{{ content.hero.lead }}</p>
          <div v-if="heroStats.length" class="hero-stats">
            <div v-for="stat in heroStats" :key="stat.label" class="hero-stat">
              <span class="hero-stat__value">{{ stat.value }}</span>
              <span class="hero-stat__label">{{ stat.label }}</span>
            </div>
          </div>
          <div class="hero-actions" v-if="showHeroActions">
            <a
              v-if="content.hero && content.hero.primaryCta"
              class="primary-btn primary-btn--shimmer"
              :href="downloadUrl"
            >
              {{ content.hero.primaryCta }}
            </a>
            <a
              v-if="content.hero && content.hero.secondaryCta"
              class="secondary-btn"
              :href="content.hero.secondaryCta.href"
            >
              {{ content.hero.secondaryCta.label }}
            </a>
          </div>
        </div>
        <div
          v-if="content.image"
          class="hero-media"
          :class="heroMediaClasses"
        >
          <span v-if="pageKey === 'home'" class="hero-icon-ring" aria-hidden="true"></span>
          <img :src="content.image" :alt="content.title" loading="eager">
          <div v-if="pageKey === 'update'" class="update-release-card" aria-label="Detail APK update">
            <span>APK siap update</span>
            <strong>v{{ appInfo.version }}</strong>
            <small>{{ appInfo.size }} - Android {{ appInfo.android }}</small>
          </div>
        </div>
      </section>

      <section v-if="content.type === 'blogIndex'" class="blog-grid-section blog-grid-section--featured">
        <a v-for="blog in sortedBlogs" :key="blog.slug" class="blog-card" :href="blog.slug">
          <img :src="blog.image" :alt="blog.title" loading="lazy">
          <div class="blog-card__content">
            <span class="blog-card__date">{{ formatBlogDate(blog.date) }}</span>
            <h2>{{ blog.title }}</h2>
            <p>{{ blog.excerpt }}</p>
            <span class="blog-card__read-more">Baca artikel</span>
          </div>
        </a>
      </section>

      <article
        v-else
        class="content-wrap"
        :class="contentWrapClasses"
      >
        <template v-for="(section, index) in content.sections">
          <div v-if="pageKey === 'home' && index > 0" :key="`wave-${index}`" class="section-wave" aria-hidden="true"></div>
          <section :key="index" :class="sectionClasses(section, index)">
            <h2 v-if="section.heading" class="section-heading">
              <span v-if="showSectionAccent" class="section-heading__accent" aria-hidden="true"></span>
              <span v-if="section.headingNumber" class="section-heading__number">{{ section.headingNumber }}. </span>
              {{ section.heading }}
            </h2>

            <p v-for="(paragraph, pIndex) in section.body" :key="`p-${pIndex}`" v-html="paragraph" />

            <div v-if="section.cards" :class="featureGridClass(section)">
              <div
                v-for="(card, cardIndex) in section.cards"
                :key="card.title"
                class="feature-card"
                :class="{ 'feature-card--wide': isBentoWide(cardIndex, section) }"
              >
                <span
                  v-if="pageKey === 'home'"
                  class="feature-card__icon"
                  :style="{ '--feature-accent': featureAccent(cardIndex) }"
                  aria-hidden="true"
                ></span>
                <h3>{{ card.title }}</h3>
                <p>{{ card.text }}</p>
              </div>
            </div>

            <ul v-if="section.list" class="clean-list">
              <li v-for="(item, itemIndex) in section.list" :key="itemIndex" v-html="item" />
            </ul>

            <p v-for="(paragraph, pIndex) in section.afterList" :key="`after-list-${pIndex}`" v-html="paragraph" />

            <div
              v-if="section.timeline && section.steps"
              class="install-timeline"
              :class="{
                'install-timeline--update': pageKey === 'update',
                'install-timeline--download': pageKey === 'download'
              }"
            >
              <div v-for="(step, stepIndex) in section.steps" :key="stepIndex" class="install-timeline__item">
                <div class="install-timeline__rail">
                  <span class="install-timeline__marker">{{ stepIndex + 1 }}</span>
                  <span v-if="stepIndex < section.steps.length - 1" class="install-timeline__line"></span>
                </div>
                <div class="install-timeline__card" v-html="step"></div>
              </div>
            </div>

            <ol v-else-if="section.steps" class="step-list">
              <li v-for="(step, stepIndex) in section.steps" :key="stepIndex" v-html="step" />
            </ol>

            <div v-if="section.table" :class="tableClass(section)">
              <table>
                <tbody v-if="!isCompareTable(section)">
                  <tr
                    v-for="(row, rowIndex) in section.table"
                    :key="rowIndex"
                    :class="{ 'is-version-row': pageKey === 'home' && row[0] === 'Versi' }"
                  >
                    <th>{{ row[0] }}</th>
                    <td>{{ row[1] }}</td>
                  </tr>
                </tbody>
                <template v-else>
                  <thead>
                    <tr>
                      <th v-for="(cell, cellIndex) in section.table[0]" :key="`head-${cellIndex}`">{{ cell }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, rowIndex) in section.table.slice(1)" :key="`row-${rowIndex}`">
                      <th>{{ row[0] }}</th>
                      <td v-for="(cell, cellIndex) in row.slice(1)" :key="`cell-${cellIndex}`">{{ cell }}</td>
                    </tr>
                  </tbody>
                </template>
              </table>
            </div>

            <div v-if="section.downloadBox" :class="downloadPanelClass()">
              <div>
                <h3>WA GB APK {{ appInfo.version }}</h3>
                <p>Size {{ appInfo.size }} - Android {{ appInfo.android }} - Gratis</p>
              </div>
              <a class="primary-btn primary-btn--pulse" :href="downloadUrl">Download WA GB APK</a>
            </div>
          </section>
        </template>

        <div v-if="pageKey === 'home'" class="section-wave" aria-hidden="true"></div>

        <section v-if="content.faqs && content.faqs.length" :class="faqSectionClass()">
          <h2 class="section-heading">
            <span v-if="showSectionAccent" class="section-heading__accent" aria-hidden="true"></span>
            {{ content.faqHeading || 'FAQ' }}
          </h2>
          <FAQ :faqs="content.faqs" />
        </section>
      </article>
    </main>

    <a
      v-if="$global.isMobileDevice() && hasDownload"
      class="fix-bottom-btn"
      :class="{ 'fix-bottom-btn--home': pageKey === 'home', 'fix-bottom-btn--guide': isGuidePage }"
      :href="downloadUrl"
    >
      Download WA GB APK
    </a>
  </div>
</template>

<script>
import '@/css/base.scss'
import '@/css/index.scss'
import '@/css/site-pages.scss'
import HeadNav from '@/components/HeadNav.vue'
import FAQ from '@/components/FAQ.vue'
import { appInfo, pages, blogs, blogIndex } from '@/documents/siteContent.js'
import { DOWNLOAD_APK_URL } from '@/config/download'

const contentByKey = {
  ...Object.keys(pages).reduce((acc, key) => {
    acc[key] = { ...pages[key], type: 'page' }
    return acc
  }, {}),
  blogIndex: { ...blogIndex, type: 'blogIndex' },
  ...blogs.reduce((acc, blog) => {
    acc[blog.key] = { ...blog, type: 'blog' }
    return acc
  }, {})
}

const FEATURE_ACCENTS = ['#2dd4bf', '#34d399', '#22d3ee', '#a78bfa', '#fbbf24', '#fb7185', '#60a5fa']

// 页面主题后缀，用于 wa-shell / breadcrumb / hero / content-wrap 等 class
const PAGE_THEMES = {
  home: 'home',
  download: 'download',
  update: 'update',
  ban: 'ban',
  iphone: 'iphone',
  pc: 'pc',
  login: 'login',
  oldVersion: 'old-version',
  about: 'about',
  contact: 'contact',
  privacy: 'privacy',
  disclaimer: 'disclaimer',
  terms: 'terms',
  blogIndex: 'blog-index',
  blogTemporaryBan: 'blog-ban',
  blogAdvantages: 'blog-features',
  blogInstallFail: 'blog-install',
  blogGbWa: 'blog-glossary',
  blogStatusNotShowing: 'blog-troubleshooting',
  blogContactsNotShowing: 'blog-troubleshooting'
}

const LEGAL_PAGES = ['about', 'contact', 'privacy', 'disclaimer', 'terms']
const BLOG_ARTICLE_PAGES = [
  'blogTemporaryBan',
  'blogAdvantages',
  'blogInstallFail',
  'blogGbWa',
  'blogStatusNotShowing',
  'blogContactsNotShowing'
]

export default {
  name: 'ContentPage',
  components: {
    HeadNav,
    FAQ
  },
  data() {
    return {
      appInfo,
      blogs,
      apk: DOWNLOAD_APK_URL,
      from: 'gb',
      filename: 'gb'
    }
  },
  computed: {
    pageKey() {
      return this.$route.meta.contentKey || 'home'
    },
    downloadUrl() {
      return this.pageKey === 'home' ? '/download-wa-gb/' : DOWNLOAD_APK_URL
    },
    pageTheme() {
      return PAGE_THEMES[this.pageKey] || null
    },
    isBlogArticle() {
      return BLOG_ARTICLE_PAGES.includes(this.pageKey)
    },
    isLegalPage() {
      return LEGAL_PAGES.includes(this.pageKey)
    },
    shellClasses() {
      const classes = {}
      if (this.pageTheme) {
        classes[`wa-shell--${this.pageTheme}`] = true
      }
      if (this.isGuidePage) {
        classes['wa-shell--guide'] = true
      }
      return classes
    },
    breadcrumbClasses() {
      return this.pageTheme ? { [`breadcrumb--${this.pageTheme}`]: true } : {}
    },
    heroSectionClasses() {
      const classes = {
        compact: this.isHeroCompact,
        'hero-section--guide': this.isGuidePage,
        'hero-section--article': this.isBlogArticle && !!this.content.image
      }
      if (this.pageTheme) {
        classes[`hero-section--${this.pageTheme}`] = true
      }
      if (this.pageKey === 'iphone') {
        classes['hero-section--iphone-wide'] = true
      }
      if (this.pageKey === 'oldVersion') {
        classes['hero-section--old-version-archive'] = true
      }
      return classes
    },
    heroMediaClasses() {
      const classes = {
        'hero-media--glass': this.pageKey === 'home',
        'hero-media--article': this.isBlogArticle
      }
      if (this.pageTheme) {
        classes[`hero-media--${this.pageTheme}`] = true
      }
      return classes
    },
    contentWrapClasses() {
      const classes = {
        'content-wrap--guide': this.isGuidePage
      }
      if (this.pageTheme && this.pageKey !== 'home' && this.pageKey !== 'blogIndex') {
        classes[`content-wrap--${this.pageTheme}`] = true
      }
      return classes
    },
    isHeroCompact() {
      if (this.content.image) return false
      if (this.pageKey === 'iphone') return false
      return true
    },
    content() {
      return contentByKey[this.pageKey] || pages.home
    },
    hasDownload() {
      return ['home', 'download', 'update'].includes(this.pageKey) || (this.content.sections || []).some((section) => section.downloadBox)
    },
    isGuidePage() {
      return this.pageKey === 'update'
    },
    showSectionAccent() {
      if (this.pageKey === 'home' || this.isGuidePage) return true
      if (['ban', 'iphone', 'pc', 'login', 'oldVersion', 'download'].includes(this.pageKey)) return true
      if (this.isLegalPage || this.isBlogArticle || this.pageKey === 'blogIndex') return true
      return false
    },
    showHeroActions() {
      return this.content.hero && (this.content.hero.primaryCta || this.content.hero.secondaryCta)
    },
    heroStats() {
      if (!['home', 'update'].includes(this.pageKey)) return []
      if (this.content.hero && this.content.hero.stats) return this.content.hero.stats
      return []
    },
    sortedBlogs() {
      return this.blogs.filter((blog) => blog.date === '2026-08-12').sort((left, right) => {
        const rightTime = new Date(right.date).getTime() || 0
        const leftTime = new Date(left.date).getTime() || 0
        return rightTime - leftTime
      })
    }
  },
  mounted() {
    this.from = this.$global.GetQueryString('from') || this.from
    this.filename = this.$global.GetQueryString('filename') || this.filename
    if (this.$global.GetQueryString('from')) {
      this.getAPK()
    } else {
      this.getOfficalApk()
    }
  },
  methods: {
    sectionClasses(section, index) {
      const classes = ['content-section']
      if (section.variant) {
        classes.push(`content-section--variant-${section.variant}`)
      }
      if (this.pageKey === 'home') {
        classes.push('content-section--home')
        classes.push(index % 2 === 0 ? 'content-section--tone-a' : 'content-section--tone-b')
        if (section.table) {
          classes.push(this.isCompareTable(section) ? 'content-section--compare' : 'content-section--spec')
        }
        if (section.downloadBox) classes.push('content-section--download')
        if (section.timeline) classes.push('content-section--install')
        return classes
      }
      if (this.isGuidePage) {
        classes.push('content-section--guide')
        if (this.pageKey === 'update') {
          classes.push('content-section--update')
          classes.push(`content-section--update-${index + 1}`)
        }
        if (section.timeline) classes.push('content-section--install')
        return classes
      }
      if (this.pageKey === 'download') {
        classes.push('content-section--download-page')
        classes.push(`content-section--download-page-${index + 1}`)
        if (section.table) classes.push('content-section--download-spec')
        if (section.downloadBox) classes.push('content-section--download-cta')
        if (section.steps) classes.push('content-section--download-install')
        return classes
      }
      if (this.pageKey === 'ban') {
        classes.push('content-section--ban')
        classes.push(`content-section--ban-${index + 1}`)
        return classes
      }
      if (this.pageKey === 'iphone') {
        classes.push('content-section--iphone')
        classes.push(`content-section--iphone-${index + 1}`)
        return classes
      }
      if (this.pageKey === 'pc') {
        classes.push('content-section--pc')
        classes.push(`content-section--pc-${index + 1}`)
        return classes
      }
      if (this.pageKey === 'login') {
        classes.push('content-section--login')
        classes.push(`content-section--login-${index + 1}`)
        return classes
      }
      if (this.pageKey === 'oldVersion') {
        classes.push('content-section--old-version')
        classes.push(`content-section--old-version-${index + 1}`)
        return classes
      }
      if (this.isLegalPage) {
        classes.push(`content-section--${this.pageTheme}`)
        classes.push(`content-section--${this.pageTheme}-${index + 1}`)
        return classes
      }
      if (this.isBlogArticle) {
        classes.push(`content-section--${this.pageTheme}`)
        classes.push(`content-section--${this.pageTheme}-${index + 1}`)
        return classes
      }
      return classes
    },
    featureGridClass(section) {
      const theme = this.pageTheme
      return {
        'feature-grid': true,
        'feature-grid--bento': this.pageKey === 'home' && section.bento,
        'feature-grid--three-four': this.pageKey === 'home' && section.layout === 'threeFour',
        'feature-grid--two-two': this.pageKey === 'home' && section.layout === 'twoTwo',
        'feature-grid--ban': this.pageKey === 'ban',
        'feature-grid--iphone': this.pageKey === 'iphone',
        'feature-grid--pc': this.pageKey === 'pc',
        'feature-grid--login': this.pageKey === 'login',
        'feature-grid--old-version': this.pageKey === 'oldVersion',
        'feature-grid--two-two':
          this.pageKey === 'oldVersion' && Array.isArray(section.cards) && section.cards.length > 0,
        'feature-grid--update': this.pageKey === 'update',
        [`feature-grid--${theme}`]: !!theme && (this.isLegalPage || this.isBlogArticle)
      }
    },
    isBentoWide(cardIndex, section) {
      return this.pageKey === 'home' && section.bento && (cardIndex === 0 || cardIndex === 3)
    },
    featureAccent(cardIndex) {
      return FEATURE_ACCENTS[cardIndex % FEATURE_ACCENTS.length]
    },
    isCompareTable(section) {
      return Array.isArray(section.table) && section.table[0] && section.table[0].length > 2
    },
    tableClass(section) {
      const isCompare = this.isCompareTable(section)
      return {
        'info-table': true,
        'info-table--device': this.pageKey === 'home' && !!section.table && !isCompare,
        'info-table--compare': this.pageKey === 'home' && isCompare,
        'info-table--pc-dark': this.pageKey === 'pc' && !!section.table,
        'info-table--download-spec': this.pageKey === 'download' && !!section.table,
        'info-table--sticky-compare': this.pageKey === 'iphone' && !!section.table
      }
    },
    downloadPanelClass() {
      return {
        'download-panel': true,
        'download-panel--glow': this.pageKey === 'home',
        'download-panel--highlight': this.pageKey !== 'home',
        'download-panel--update': this.pageKey === 'update'
      }
    },
    faqSectionClass() {
      const theme = this.pageTheme
      const classes = {
        'content-section': true,
        'faq-section': true,
        'content-section--home': this.pageKey === 'home',
        'content-section--guide': this.isGuidePage,
        'content-section--ban': this.pageKey === 'ban',
        'content-section--iphone': this.pageKey === 'iphone',
        'content-section--pc': this.pageKey === 'pc',
        'content-section--login': this.pageKey === 'login',
        'content-section--old-version': this.pageKey === 'oldVersion',
        'content-section--update': this.pageKey === 'update',
        'content-section--download-page': this.pageKey === 'download',
        'content-section--tone-a': this.pageKey === 'home',
        'faq-section--guide': this.isGuidePage,
        'faq-section--ban': this.pageKey === 'ban',
        'faq-section--iphone': this.pageKey === 'iphone',
        'faq-section--pc': this.pageKey === 'pc',
        'faq-section--login': this.pageKey === 'login',
        'faq-section--old-version': this.pageKey === 'oldVersion',
        'faq-section--update': this.pageKey === 'update'
      }
      if (theme && (this.isLegalPage || this.isBlogArticle)) {
        classes[`content-section--${theme}`] = true
        classes[`faq-section--${theme}`] = true
      }
      return classes
    },
    getAPK() {
      this.apk = DOWNLOAD_APK_URL
    },
    getOfficalApk() {
      this.apk = DOWNLOAD_APK_URL
    },
    downloadApk() {
      const target = this.apk || DOWNLOAD_APK_URL
      if (target) {
        this.$global.download(target)
      }
    },
    formatBlogDate(date) {
      const parsedDate = new Date(date)
      if (Number.isNaN(parsedDate.getTime())) return date
      return parsedDate.toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    }
  }
}
</script>
